import moment from 'moment';
import { TimeZoneHelper } from './timeZoneHelper';

export class MeetingTimeZoneHelper {
    /**
     * The short name of the zone the viewer's browser shows `date` in, e.g. 'CDT'
     * (outside the US the browser gives an offset such as 'GMT+3').
     */
    public static getViewerTimeZoneLabel(date: Date | string): string {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZoneName: 'short',
        }).formatToParts(new Date(date));
        return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    }

    /** A meeting time in the viewer's zone with that zone named, e.g. '10:00 AM CDT'. */
    public static formatTime(date: Date | string): string {
        return `${moment(date).format('h:mm A')} ${this.getViewerTimeZoneLabel(
            date
        )}`.trim();
    }

    /**
     * The note for a meeting the viewer sees in another zone than the one it was scheduled in,
     * e.g. 'Scheduled for 10/06/2026 10:00 AM – 11:00 AM CDT'. Undefined when the zones match,
     * when no zone was recorded, or when the recorded zone is unknown.
     *
     * @param viewerOffsetMinutes the viewer's offset as Date.getTimezoneOffset() gives it at the
     * start; defaults to the browser's.
     */
    public static getScheduledTimeNote(
        dateStart: Date | string,
        dateEnd: Date | string,
        scheduledTimeZone?: string | null,
        viewerOffsetMinutes?: number
    ): string | undefined {
        if (!scheduledTimeZone || !dateStart || !dateEnd) {
            return undefined;
        }

        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(scheduledTimeZone);
        if (!zone) {
            return undefined;
        }

        const start = new Date(dateStart);
        const end = new Date(dateEnd);
        const viewerOffsetHours =
            -(viewerOffsetMinutes ?? start.getTimezoneOffset()) / 60;
        if (viewerOffsetHours === zone.offset) {
            return undefined;
        }

        const zoneStart = moment.utc(start).add(zone.offset, 'hours');
        const zoneEnd = moment.utc(end).add(zone.offset, 'hours');
        const endFormat = zoneStart.isSame(zoneEnd, 'day')
            ? 'h:mm A'
            : 'MM/DD/YYYY h:mm A';

        return `Scheduled for ${zoneStart.format(
            'MM/DD/YYYY h:mm A'
        )} – ${zoneEnd.format(endFormat)} ${zone.abbreviation}`;
    }
}
